import { FC, lazy, useMemo } from 'react';
import { ContactsParams } from 'src/apis/organisation-service/types';
import { PageSpinner } from 'src/components/spinner-container';
import { Successful } from 'src/hooks/use-axios';
import {
  OrganisationDetailsRequest,
  useOrganisationDetails,
} from 'src/pages/settings/organisation/company-information/use-organisation-details';
import { useContacts } from 'src/pages/settings/organisation/contacts/use-contacts';
import { usePageParams } from 'src/pages/settings/organisation/contacts/use-page-params';
import { OrganisationServiceRequest } from 'src/pages/settings/organisation/use-organisation-service';
import { Forbidden } from 'src/utils/errors';

const Page = lazy(() => import('./page'));

type Props = {
  organisationService: Successful<OrganisationServiceRequest>;
};

export const ContactsOverview: FC<Props> = ({ organisationService }) => {
  if (!organisationService.response.data._links.getMyOrganisationDetails)
    throw new Forbidden();

  const organisationDetails = useOrganisationDetails({
    url: organisationService.response.data._links.getMyOrganisationDetails.href,
  });

  if (!organisationDetails.response) return <PageSpinner />;

  return <Contacts organisationDetails={organisationDetails} />;
};

type ContactProps = {
  organisationDetails: Successful<OrganisationDetailsRequest>;
};

const Contacts: FC<ContactProps> = ({ organisationDetails }) => {
  if (!organisationDetails.response.data._links.contacts) throw new Forbidden();

  const pageParams = usePageParams();

  const params = useMemo(
    () =>
      ({
        limit: pageParams.value.pageSize,
        offset: pageParams.value.start,
      }) satisfies ContactsParams,
    [pageParams.value]
  );

  const contacts = useContacts({
    url: organisationDetails.response.data._links.contacts.href,
    params,
  });

  if (!contacts.response) return <PageSpinner />;

  return <Page pageParams={pageParams} contacts={contacts} />;
};
